import { useProjectId } from "../hooks/useProjectId";
import { useState } from "react";
import { Box, Card, Stack, Title, ScrollArea, Text, Tabs } from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import {
  FaSliders,
  FaPalette,
  FaWind,
  FaShapes,
  FaDatabase,
  FaRotateLeft,
  FaArrowsRotate,
} from "react-icons/fa6";
import ArticleValidationSettingsCard from "./ArticleValidationSettingsCard";
import DrofusSettingsCard from "./DrofusSettings";
import ProjectBasicsCard from "./ProjectBasicsCard";
import { ClearValidationButton } from "./ClearValidationButton";
import VentilationSettingsCard from "./VentilationSettingsCard";
import DeviceTypesOverviewCard from "./DeviceTypesOverviewCard";
import ArticleColoringSettingsCard from "./ArticleColoringSettingsCard";
import { RoomSyncSettingsCard } from "./RoomSyncSettingsCard";
import { OccurrenceSyncSettingsCard } from "./OccurrenceSyncSettingsCard";
import styles from "./SettingsPage.module.css";

const sections = [
  {
    id: "general",
    label: "Generelt",
    description: "Prosjekt og modellfelt",
    icon: FaSliders,
    component: ProjectBasicsCard,
  },
  {
    id: "synchronization",
    label: "Synkronisering",
    description: "Rom, forekomster og artikler",
    icon: FaArrowsRotate,
    component: SynchronizationSettings,
  },
  {
    id: "article-colors",
    label: "Artikkelfarger",
    description: "Fargefiltre og egenskaper",
    icon: FaPalette,
    component: ArticleColoringSettingsCard,
  },
  {
    id: "drofus",
    label: "dRofus",
    description: "Tilkobling og valideringsunntak",
    icon: FaDatabase,
    component: DrofusSettingsSection,
  },
  {
    id: "ventilation",
    label: "Ventilasjon",
    description: "Regler for tilluft og avtrekk",
    icon: FaWind,
    component: VentilationSettingsCard,
  },
  {
    id: "devices",
    label: "Komponenttyper",
    description: "Oversikt over IFC-kategorier",
    icon: FaShapes,
    component: DeviceTypesOverviewCard,
  },
  {
    id: "validation",
    label: "Validering",
    description: "Tilbakestill valideringsdata",
    icon: FaRotateLeft,
    component: ValidationSettings,
  },
];

function SynchronizationSettings() {
  const projectNo = useProjectId();
  return (
    <Stack gap="md" key={projectNo}>
      <RoomSyncSettingsCard />
      <OccurrenceSyncSettingsCard />
    </Stack>
  );
}

function DrofusSettingsSection() {
  return (
    <Stack gap="md">
      <DrofusSettingsCard />
      <ArticleValidationSettingsCard />
    </Stack>
  );
}

function ValidationSettings() {
  return (
    <Card withBorder radius="lg" p="lg">
      <Stack gap="sm" align="flex-start">
        <Title order={4}>Fjern dRofus-valideringer</Title>
        <Text size="sm" c="dimmed">
          Fjern alle valideringer for dette prosjektet. Kan ikke angres.
        </Text>
        <ClearValidationButton />
      </Stack>
    </Card>
  );
}

export default function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("settingsTab");
  const active = sections.find((section) => section.id === requested)?.id ?? "general";
  const [visited, setVisited] = useState<string[]>([active]);

  function selectSection(value: string | null) {
    if (!value) return;
    setVisited((previous) => (previous.includes(value) ? previous : [...previous, value]));
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("settingsTab", value);
      return next;
    });
  }

  return (
    <ScrollArea.Autosize w="100%" mah="calc(100vh - 72px)" type="auto" offsetScrollbars>
      <Box className={styles.page}>
        <Stack gap={6} mb="xl">
          <Title order={2}>Prosjektinnstillinger</Title>
          <Text c="dimmed" size="sm">
            Velg en kategori for å administrere prosjektet.
          </Text>
        </Stack>
        <Tabs
          value={active}
          onChange={selectSection}
          orientation="vertical"
          classNames={{
            root: styles.layout,
            list: styles.navigation,
            tab: styles.tab,
            panel: styles.panel,
          }}
        >
          <Tabs.List aria-label="Innstillingskategorier">
            {sections.map(({ id, label, description, icon: Icon }) => (
              <Tabs.Tab key={id} value={id} leftSection={<Icon size={16} aria-hidden />}>
                <Text size="sm" fw={600}>
                  {label}
                </Text>
                <Text size="xs" c="dimmed" className={styles.description}>
                  {description}
                </Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {sections.map(({ id, label, description, component: Component }) => (
            <Tabs.Panel key={id} value={id}>
              {(visited.includes(id) || active === id) && (
                <Stack gap="md">
                  <Box>
                    <Title order={3}>{label}</Title>
                    <Text size="sm" c="dimmed">
                      {description}
                    </Text>
                  </Box>
                  <Component />
                </Stack>
              )}
            </Tabs.Panel>
          ))}
        </Tabs>
      </Box>
    </ScrollArea.Autosize>
  );
}
